import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  LogOut, 
  CreditCard, 
  FileText, 
  MessageSquare, 
  AlertTriangle,
  ChevronRight,
  Loader2,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Debtor, Case, PaymentPlan } from '../types';

interface DashboardPageProps {
  debtor: Debtor;
  onLogout: () => void;
}

export default function DashboardPage({ debtor, onLogout }: DashboardPageProps) {
  const [cases, setCases] = useState<Case[]>([]);
  const [activePlan, setActivePlan] = useState<PaymentPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalDebt, setTotalDebt] = useState(0);

  useEffect(() => {
    loadDashboardData();
  }, [debtor.debtor_id]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load cases
      const casesResult = await serenityClient.queryData<Case>(
        `SELECT * FROM Cases WHERE debtor_id = '${debtor.debtor_id}' ORDER BY total_amount DESC`
      );
      setCases(casesResult);
      
      // Calculate total debt
      const total = casesResult.reduce((sum, c) => {
        if (!['paid_in_full', 'settled', 'write_off'].includes(c.status)) {
          return sum + (c.total_amount - (c.amount_paid || 0));
        }
        return sum;
      }, 0);
      setTotalDebt(total);

      // Load active payment plan
      const plansResult = await serenityClient.queryData<PaymentPlan>(
        `SELECT * FROM PaymentPlans WHERE debtor_id = '${debtor.debtor_id}' AND status = 'active' LIMIT 1`
      );
      setActivePlan(plansResult[0] || null);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
      case 'in_contact':
        return 'bg-blue-100 text-blue-800';
      case 'negotiating':
      case 'arrangement':
        return 'bg-yellow-100 text-yellow-800';
      case 'paying':
        return 'bg-green-100 text-green-800';
      case 'broken_arrangement':
      case 'pre_legal':
      case 'legal':
        return 'bg-red-100 text-red-800';
      case 'paid_in_full':
      case 'settled':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">DFS Collections</h1>
                <p className="text-xs text-gray-500">Debtor Portal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  {debtor.debtor_type === 'corporate' ? debtor.first_name : `${debtor.first_name} ${debtor.last_name || ''}`}
                </p>
                <p className="text-xs text-gray-500">{debtor.email}</p>
              </div>
              <button
                onClick={onLogout}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Debt */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-500">Total Outstanding</h3>
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalDebt)}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {cases.filter(c => !['paid_in_full', 'settled', 'write_off'].includes(c.status)).length} active case(s)
                </p>
              </div>

              {/* Active Payment Plan */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-500">Payment Plan</h3>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                {activePlan ? (
                  <>
                    <p className="text-3xl font-bold text-gray-900">
                      {formatCurrency(activePlan.installment_amount || 0)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {activePlan.installment_frequency} payment
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xl font-semibold text-gray-400">No active plan</p>
                    <Link to="/payment" className="text-sm text-blue-600 hover:underline mt-1 inline-block">
                      Set up a payment plan →
                    </Link>
                  </>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Link
                    to="/chat"
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Chat with Assistant</span>
                  </Link>
                  <Link
                    to="/documents"
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Upload Documents</span>
                  </Link>
                  <Link
                    to="/hardship"
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <span className="text-sm font-medium text-gray-700">Request Hardship</span>
                  </Link>
                </div>
              </div>
            </div>

            {/* Cases List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Your Cases</h2>
              </div>
              
              {cases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No cases found.
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {cases.map((caseItem) => (
                    <Link
                      key={caseItem.case_id}
                      to={`/case/${caseItem.case_id}`}
                      className="block p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold text-gray-900">{caseItem.case_id}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(caseItem.status)}`}>
                              {getStatusLabel(caseItem.status)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>Origin: {caseItem.origin}</span>
                            <span>Creditor: {caseItem.cedent}</span>
                            <span>{caseItem.days_overdue} days overdue</span>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="text-lg font-bold text-gray-900">
                              {formatCurrency(caseItem.total_amount - (caseItem.amount_paid || 0), caseItem.currency)}
                            </p>
                            {caseItem.amount_paid && caseItem.amount_paid > 0 && (
                              <p className="text-xs text-green-600">
                                {formatCurrency(caseItem.amount_paid, caseItem.currency)} paid
                              </p>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Make Payment CTA */}
            {totalDebt > 0 && (
              <div className="mt-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold mb-1">Ready to make a payment?</h3>
                    <p className="text-blue-100">Pay online securely or set up a payment plan that works for you.</p>
                  </div>
                  <Link
                    to={cases[0] ? `/payment/${cases[0].case_id}` : '/payment'}
                    className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
                  >
                    <CreditCard className="w-5 h-5" />
                    Make Payment
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
